import React, { useState } from "react";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Fake events (no library)
const EVENTS: Record<string, string[]> = {
  "2026-01-03": ["Design Review"],
  "2026-01-10": ["Sprint Planning"],
  "2026-01-15": ["Product Launch"],
  "2026-01-21": ["Client Meeting", "QA Check"],
};

const ComplianceCalender = ({ data }: { data: any }) => {
  const [date, setDate] = useState(new Date());
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    filing: string;
    due: string;
  } | null>(null);

  const year = date.getFullYear();
  const month = date.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setDate(new Date(year, month - 1, 1));
  const nextMonth = () => setDate(new Date(year, month + 1, 1));

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const formatDateKey = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const eventsByDate: Record<string, any[]> = {};

  data?.forEach((item: any) => {
    if (!item?.due_date) return;

    const key = formatDateKey(item.due_date);
    if (!eventsByDate[key]) eventsByDate[key] = [];
    eventsByDate[key].push(item);
  });

  const formatTooltipDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "14px",
      }}
    >
      <div
        style={{
          width: "100%",
          border: "1px solid #dadadaff",
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#ffffffff",
          color: "#ffffff",
          boxShadow: "0 15px 20px -12px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <button
            onClick={prevMonth}
            style={{
              padding: "4px 12px",
              borderRadius: "6px",
              background: "white",
              color: "#2977cc",
              cursor: "pointer",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#f7f7f7ff")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "white")
            }
          >
            <ArrowBackIosNewOutlinedIcon fontSize="small" />
          </button>

          <h2
            style={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#424242ff",
            }}
          >
            {date.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </h2>

          <button
            onClick={nextMonth}
            style={{
              padding: "4px 12px",
              borderRadius: "6px",
              background: "white",
              color: "#2977cc",
              cursor: "pointer",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#f7f7f7ff")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "white")
            }
          >
            <ArrowForwardIosOutlinedIcon fontSize="small" />
          </button>
        </div>

        {/* Days */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            backgroundColor: "#f5f5f5ff",
            textAlign: "center",
            fontSize: "14px",
            fontWeight: 600,
            color: "#2977cc",
          }}
        >
          {WEEK_DAYS.map((day) => (
            <div key={day} style={{ padding: "12px 0" }}>
              {day}
            </div>
          ))}
        </div>

        {/* Dates */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "1px",
            backgroundColor: "#dfdfdfff",
          }}
        >
          {cells.map((day, idx) => {
            const key = day
              ? `${year}-${String(month + 1).padStart(2, "0")}-${String(
                  day
                ).padStart(2, "0")}`
              : null;

            return (
              <div
                key={idx}
                style={{
                  minHeight: "110px",
                  backgroundColor: "#fafafaff",
                  padding: "8px",
                }}
              >
                {day && (
                  <>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        marginBottom: "4px",
                        color: "#000",
                        overflow: "none",
                      }}
                    >
                      {day}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        overflow: "auto",
                        height: "75px",
                      }}
                    >
                      {key &&
                        eventsByDate[key]?.map((event, i) => (
                          <div
                            key={i}
                            style={{
                              position: "relative",
                              backgroundColor: "rgba(41,119,204,0.12)",
                              color: "#2977cc",
                              fontSize: "12px",
                              padding: "10px 14px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontWeight: 500,
                            }}
                            onMouseEnter={(e) => {
                              setTooltip({
                                x: e.clientX + 12,
                                y: e.clientY + 12,
                                filing: formatTooltipDate(event.filing_date),
                                due: formatTooltipDate(event.due_date),
                              });
                            }}
                            onMouseMove={(e) => {
                              setTooltip((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      x: e.clientX + 12,
                                      y: e.clientY + 12,
                                    }
                                  : null
                              );
                            }}
                            onMouseLeave={() => setTooltip(null)}
                          >
                            {event.order_type || "N/A"}

                            {/* Tooltip */}
                            {tooltip && (
                              <div
                                style={{
                                  ...floatingTooltipStyle,
                                  top: tooltip.y,
                                  left: tooltip.x,
                                }}
                              >
                                <div
                                  style={{
                                    marginBottom: "4px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <strong>Filing Date:</strong> {tooltip.filing}
                                </div>
                                <div style={{ whiteSpace: "nowrap" }}>
                                  <strong>Due Date:</strong> {tooltip.due}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ComplianceCalender;

const floatingTooltipStyle: React.CSSProperties = {
  position: "fixed",
  backgroundColor: "#ecececff",
  color: "#272727ff",
  padding: "8px 10px",
  borderRadius: "6px",
  fontSize: "11px",
  zIndex: 9999,
  boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
  pointerEvents: "none",
};
